import { useEffect, useState } from "react";
import { Eye, History, Download, Loader2, RefreshCw } from "lucide-react";
import { AdminEligibility } from "@/lib/api";
import { LetterVersionTools } from "@/components/LetterVersionTools";

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 py-1 border-b border-white/[0.06] text-xs">
    <span className="text-white/55">{k}</span>
    <span className="text-white/90 text-right wrap-anywhere">{String(v ?? "—")}</span>
  </div>
);

/** Master v1.0 controls: Preview Assessment · Regenerate (versioned) · Generation History. */
export const LetterEnginePanel = ({ check, hasLetter, onRegenerated }) => {
  const [preview, setPreview] = useState(null);
  const [versions, setVersions] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (hasLetter) AdminEligibility.letterVersions(check.id).then((r) => setVersions(r.versions)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check.id, hasLetter]);

  const run = async (key, fn) => {
    setBusy(key);
    setErr("");
    try {
      await fn();
    } catch (e) {
      setErr(e?.response?.data?.detail?.message_en || e?.response?.data?.detail || "Request failed");
    } finally {
      setBusy("");
    }
  };

  const doPreview = () => run("preview", async () => setPreview(await AdminEligibility.assessmentPreview(check.id)));
  const doHistory = () => run("history", async () => setVersions((await AdminEligibility.letterVersions(check.id)).versions));
  const doRegenerate = () => {
    const reason = window.prompt("Reason for regeneration (recorded in audit history):");
    if (!reason) return;
    return run("regen", async () => {
      try {
        await AdminEligibility.generateLetter(check.id, reason);
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 409 && d?.code === "REVIEW_CONFIRMATION_REQUIRED") {
          if (!window.confirm(`Overridden fields: ${(d.overridden_fields || []).join(", ")}\n\nGenerate Final Eligibility Letter Using These Reviewed Values?`)) throw e;
          await AdminEligibility.generateLetterConfirmed(check.id, reason);
        } else throw e;
      }
      onRegenerated?.();
      setVersions((await AdminEligibility.letterVersions(check.id)).versions);
    });
  };
  const openVersion = (v) =>
    run(`v-${v.id}`, async () => window.open(await AdminEligibility.letterVersionBlobUrl(check.id, v.id), "_blank"));

  const a = preview?.assessment;
  return (
    <div data-testid="letter-engine-panel" className="mt-3 rounded-xl border border-[#D4AF37]/25 bg-black/20 p-3">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
        Eligibility Letter Engine · Master v1.0 · Locked baseline
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <button data-testid="letter-preview-assessment" onClick={doPreview} disabled={!!busy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-xs text-white/85 hover:border-[#D4AF37] disabled:opacity-50">
          {busy === "preview" ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Preview Assessment
        </button>
        {hasLetter && (
          <button data-testid="letter-regenerate" onClick={doRegenerate} disabled={!!busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-xs text-white/85 hover:border-[#D4AF37] disabled:opacity-50">
            {busy === "regen" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Regenerate · New Version
          </button>
        )}
        <button data-testid="letter-history" onClick={doHistory} disabled={!!busy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-xs text-white/85 hover:border-[#D4AF37] disabled:opacity-50">
          {busy === "history" ? <Loader2 size={13} className="animate-spin" /> : <History size={13} />} Generation History
        </button>
      </div>
      {err && <div data-testid="letter-engine-error" className="mt-2 text-xs text-rose-300">{String(err)}</div>}

      {a && (
        <div data-testid="assessment-preview" className="mt-3 grid sm:grid-cols-2 gap-x-6">
          <div>
            <Row k="File status" v={a.file_status} />
            <Row k="Income status" v={a.income_status === "VERIFIED" ? "VERIFIED" : "VERIFIED INCOME NOT ESTABLISHED"} />
            <Row k="FOIR" v={a.foir != null ? `${a.foir}%` : "NOT FINALIZABLE"} />
            <Row k="Liabilities" v={a.liabilities_note} />
            <Row k="Security-deposit route" v={a.security_route_available ? "Available" : "Subject to written offer"} />
            <Row k="Bureau score" v={preview.bureau?.score} />
            <Row k="Engine" v={`${preview.template_version} · ${preview.engine_version} · ${preview.ai_source}`} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">Warnings / exceptions</div>
            <ul className="mt-1 space-y-1 text-xs text-amber-200/90 list-disc pl-4">
              {(a.exceptions?.length ? a.exceptions : ["None detected on available evidence"]).map((x) => <li key={x}>{x}</li>)}
            </ul>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mt-3">Documents reviewed</div>
            <div className="text-xs text-white/80 mt-1">
              {(preview.documents_reviewed || []).map((d) => d.label || d.filename).join(", ") || "None uploaded"}
            </div>
          </div>
        </div>
      )}

      {versions && (
        <div data-testid="letter-versions" className="mt-3">
          {versions.length === 0 && <div className="text-xs text-white/55">No versions generated yet.</div>}
          {versions.map((v) => (
            <div key={v.id} data-testid={`letter-version-${v.version_no}`}
              className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-white/[0.06] text-xs">
              <div className="min-w-0">
                <span className="font-semibold text-[#F0D788]">v{v.version_no}</span>
                <span className="text-white/70"> · {v.reference_no} · {new Date(v.generation_timestamp).toLocaleString()} · {v.generated_by}</span>
                <div className="text-white/45">{v.eligibility_letter_template_version} · {v.underwriting_engine_version} · sha256 {String(v.pdf_sha256).slice(0, 12)}…{v.regeneration_reason ? ` · Reason: ${v.regeneration_reason}` : ""}</div>
              </div>
              <button data-testid={`letter-version-pdf-${v.version_no}`} onClick={() => openVersion(v)} disabled={!!busy}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-white/15 text-white/85 hover:border-[#D4AF37] disabled:opacity-50">
                <Download size={12} /> PDF
              </button>
            </div>
          ))}
        </div>
      )}
      {hasLetter && <LetterVersionTools check={check} versions={versions || []} onSent={onRegenerated} />}
    </div>
  );
};
